import {
  parseJsonEventStream,
  type UIMessageChunk,
  uiMessageChunkSchema,
} from "ai";

/**
 * Synthetic chunk type the chat stream route inserts after replaying every
 * chunk that existed when a client attached. Clients use it to flip the
 * smoothing gate so live deltas can stream after the buffered replay is
 * over. The marker is NOT part of the workflow chunk sequence, so it must
 * not advance `chunkIndex`.
 */
export const RESUME_REPLAY_COMPLETE_TYPE = "data-resume-replay-complete";

/**
 * Hard ceiling on reconnect-loop iterations. We expect 1-2 iterations in
 * practice (one fetch returns the full replayed buffer + live deltas; if
 * the server cycles its long-poll we go around again). More than this and
 * something is wrong — better to surface an error than to spin forever.
 *
 * Exported so callers and tests share a single source of truth.
 */
export const MAX_RECONNECT_ATTEMPTS = 8;

/**
 * How many *consecutive* transient transport failures (a dropped socket, a
 * 5xx/429/408 from a cold edge, a truncated chunk) the iterator tolerates
 * before giving up and surfacing a terminal error (which the client renders
 * as the transport-error banner). Reset to zero whenever an attempt makes
 * progress, so a brief blip mid-stream is invisible to the user and only a
 * connection that *keeps* failing trips the banner (ADR-049 Rule 4).
 *
 * Distinct from {@link MAX_RECONNECT_ATTEMPTS}: that bounds total iterations
 * (incl. healthy long-poll cycles) to guard against an infinite spin; this
 * bounds the retry budget for the failure path specifically.
 */
export const MAX_CONSECUTIVE_RECONNECT_ERRORS = 5;

/**
 * Backoff before the Nth consecutive retry. Exponential, capped at 4s, so a
 * one-second blip self-heals on the first retry while a sustained outage
 * still surfaces within a few seconds. Deterministic (no jitter) so tests can
 * assert it directly.
 */
export function reconnectBackoffMs(consecutiveErrors: number): number {
  const n = Math.max(1, consecutiveErrors);
  return Math.min(300 * 2 ** (n - 1), 4000);
}

/**
 * Marker error for failures that will never recover by retrying (the run id
 * is gone / invalid, or the server reported a clean empty close). The retry
 * loop rethrows these immediately instead of burning the retry budget.
 */
class TerminalReconnectError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "TerminalReconnectError";
  }
}

/**
 * Retryable HTTP statuses: transient overload / timeout / rate-limit. Every
 * other non-2xx (404 run-gone, 401/403 auth, 400 bad request) is terminal —
 * retrying it just delays the inevitable error.
 */
function isRetryableStatus(status: number): boolean {
  return status === 408 || status === 429 || status >= 500;
}

function isAbortError(err: unknown): boolean {
  return err instanceof Error && err.name === "AbortError";
}

// Plain Error rather than DOMException so the helper works in React Native
// (mobile) too, where DOMException is not reliably available.
function abortError(signal?: AbortSignal): unknown {
  if (signal?.reason !== undefined) {
    return signal.reason;
  }
  const err = new Error("Aborted");
  err.name = "AbortError";
  return err;
}

function defaultSleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortError(signal));
      return;
    }
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        clearTimeout(timer);
        reject(abortError(signal));
      },
      { once: true }
    );
  });
}

export interface ReconnectIteratorOptions {
  /** Optional AbortSignal threaded through every fetch. */
  abortSignal?: AbortSignal;
  /**
   * Base URL of the chat stream endpoint. The path
   * `/{runId}/stream?startIndex=…&replayBoundary=…` is appended.
   * Example: `https://example.com/api/chat`
   */
  baseUrl: string;
  /** Fetch implementation. Tests inject a stub; mobile passes its auth fetch. */
  fetch: typeof globalThis.fetch;
  /**
   * Max consecutive transient failures before surfacing a terminal error.
   * Defaults to {@link MAX_CONSECUTIVE_RECONNECT_ERRORS}.
   */
  maxConsecutiveErrors?: number;
  /**
   * Hook for callers to clean up per-chat run-id maps once the stream
   * exits cleanly (i.e. produced a finish chunk).
   */
  onCleanFinish?: () => void;
  /** Called once per session when the replay-boundary marker arrives. */
  onResumeReplayComplete?: () => void;
  /** Fresh stores need the reply even if the run finished during page load. */
  replayFromStart?: boolean;
  /** Workflow run id to resume against. May not be falsy. */
  runId: string;
  /**
   * Sleep used for backoff between retries. Injectable so tests run without
   * real timers. Defaults to a setTimeout-based, abort-aware delay.
   */
  sleep?: (ms: number, signal?: AbortSignal) => Promise<void>;
}

/**
 * Generator that drives the chat reconnect loop. Conforms to
 * docs/adr/049-chat-stream-reconnect-contract.md:
 *
 * - Bounded by {@link MAX_RECONNECT_ATTEMPTS} (total iterations).
 * - Treats `{ type: "finish" }` and `{ type: "error" }` as terminal;
 *   yields the chunk and exits the outer loop.
 * - Treats an attempt that produced zero chunks without a finish as a
 *   terminal failure (the run is gone — don't spin).
 * - Retries transient transport failures (network drop, 5xx/429/408,
 *   truncated chunk) with exponential backoff up to
 *   {@link MAX_CONSECUTIVE_RECONNECT_ERRORS}, resetting the counter on
 *   progress. Only a connection that keeps failing surfaces a terminal
 *   error (ADR-049 Rule 4) — a one-second blip is invisible to the user.
 * - Filters the synthetic `RESUME_REPLAY_COMPLETE_TYPE` marker before
 *   handing chunks to AI SDK, and does not advance the chunk cursor for it.
 * - Calls `onCleanFinish` once after a finish chunk.
 *
 * Extracted from `apps/mobile/lib/chat-transport.ts` so it can be
 * unit-tested with a stub fetch. The mobile transport wires this up with
 * its closure (chatId, workflowRunIds map, AbstractChat call frame); the
 * shape of the function is platform-agnostic.
 */
export async function* reconnectIterator(
  options: ReconnectIteratorOptions
): AsyncGenerator<UIMessageChunk, void, void> {
  const {
    runId,
    baseUrl,
    fetch,
    abortSignal,
    onResumeReplayComplete,
    onCleanFinish,
    maxConsecutiveErrors = MAX_CONSECUTIVE_RECONNECT_ERRORS,
    sleep = defaultSleep,
  } = options;

  let chunkIndex = 0;
  let gotFinish = false;
  let needsReplayBoundary = true;
  let attempts = 0;
  // Consecutive transient failures. Reset to zero on any attempt that makes
  // progress so a mid-stream blip never escalates to a banner.
  let consecutiveErrors = 0;

  while (!gotFinish) {
    if (attempts >= MAX_RECONNECT_ATTEMPTS) {
      throw new Error(
        `Stream reconnect exceeded ${MAX_RECONNECT_ATTEMPTS} attempts without receiving a finish chunk (runId=${runId}, chunkIndex=${chunkIndex})`
      );
    }
    attempts += 1;

    try {
      yield* attemptReconnect();
      // The attempt drained without throwing. If it produced chunks but no
      // finish, the server cycled its long-poll — that's healthy progress,
      // so clear the failure budget before looping for the next window.
      consecutiveErrors = 0;
    } catch (err) {
      // Aborts (the user navigated away / hit stop) and genuinely terminal
      // failures must propagate immediately — retrying them is pointless and
      // would mask a real error behind a few seconds of false hope.
      if (
        err instanceof TerminalReconnectError ||
        isAbortError(err) ||
        abortSignal?.aborted
      ) {
        throw err;
      }

      consecutiveErrors += 1;
      if (consecutiveErrors >= maxConsecutiveErrors) {
        throw new Error(
          `Stream reconnect failed after ${maxConsecutiveErrors} consecutive transport errors (runId=${runId}, chunkIndex=${chunkIndex})`,
          { cause: err }
        );
      }
      await sleep(reconnectBackoffMs(consecutiveErrors), abortSignal);
    }
  }

  onCleanFinish?.();

  /**
   * One fetch → parse → drain cycle. Advances `chunkIndex`/`gotFinish`/
   * `needsReplayBoundary` in the closure. Throws a {@link
   * TerminalReconnectError} for non-recoverable failures and a plain `Error`
   * for transient ones, which the caller's catch classifies.
   */
  async function* attemptReconnect(): AsyncGenerator<
    UIMessageChunk,
    void,
    void
  > {
    const params = new URLSearchParams({ startIndex: String(chunkIndex) });
    if (options.replayFromStart && chunkIndex === 0) {
      params.set("replayFromStart", "1");
    }
    if (needsReplayBoundary) {
      params.set("replayBoundary", "1");
    }

    const res = await fetch(
      `${baseUrl}/${encodeURIComponent(runId)}/stream?${params}`,
      { signal: abortSignal }
    );

    if (!res.ok || !res.body) {
      // Bound the upstream body read — a misbehaving (or malicious)
      // upstream could otherwise stream a multi-MB error body and we
      // would buffer all of it just to put it in an Error message.
      const message = `Failed to fetch chat: ${res.status} ${await readBoundedBody(res)}`;
      if (res.body && isRetryableStatus(res.status)) {
        throw new Error(message);
      }
      throw new TerminalReconnectError(message);
    }

    const chunkStream = parseJsonEventStream({
      stream: res.body,
      schema: uiMessageChunkSchema,
    });

    let sawChunkThisAttempt = false;
    const reader = chunkStream.getReader();
    try {
      while (true) {
        const { done, value: chunk } = await reader.read();
        if (done) {
          break;
        }
        sawChunkThisAttempt = true;
        if (!chunk.success) {
          // A parse failure mid-stream is almost always a truncated chunk
          // from a dropped connection — transient, so let the retry loop
          // resume from `chunkIndex`.
          throw chunk.error;
        }
        const value = chunk.value as UIMessageChunk;

        if (value.type === RESUME_REPLAY_COMPLETE_TYPE) {
          needsReplayBoundary = false;
          onResumeReplayComplete?.();
          continue;
        }

        chunkIndex += 1;
        yield value;

        // Both `finish` and `error` are terminal in the AI SDK's UI
        // chunk contract: the stream emits one and closes. Break the
        // inner reader loop on either so we don't yield (or chunk-
        // index) any post-terminal chunks the server might send by
        // mistake, and so the outer loop exits via `gotFinish`.
        if (value.type === "finish" || value.type === "error") {
          gotFinish = true;
          break;
        }
      }
    } finally {
      reader.releaseLock();
    }

    if (!sawChunkThisAttempt && !gotFinish) {
      // ADR-049 Rule 2: an empty close without a finish means the server-side
      // run is gone (already complete, or wedged). Terminal — don't retry.
      throw new TerminalReconnectError(
        `Stream reconnect closed without finish chunk (runId=${runId}, chunkIndex=${chunkIndex})`
      );
    }
  }
}

/**
 * Read at most {@link MAX_ERROR_BODY_BYTES} of an HTTP error body. Used
 * only for surfacing fetch failures in error messages — the body is
 * not consumed for any other purpose, so truncation is safe.
 */
const MAX_ERROR_BODY_BYTES = 1024;

async function readBoundedBody(res: Response): Promise<string> {
  if (!res.body) {
    return "";
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let received = 0;
  let out = "";
  let truncated = false;
  try {
    while (true) {
      if (received >= MAX_ERROR_BODY_BYTES) {
        truncated = true;
        break;
      }
      const { done, value } = await reader.read();
      if (done) {
        break;
      }
      received += value.byteLength;
      out += decoder.decode(value, { stream: true });
    }
    out += decoder.decode();
  } finally {
    // `cancel()` both releases the reader lock and closes the
    // underlying source, so any unread bytes are dropped and the
    // connection can be reclaimed.
    await reader.cancel().catch(() => {
      /* already closed */
    });
  }
  return truncated ? `${out.slice(0, MAX_ERROR_BODY_BYTES)}…(truncated)` : out;
}
